import {
  showBrowserNotification,
  wasWebPushDisabled,
} from "@/lib/webPush";

const STORAGE_KEY = "carrfields-listing-expiry-reminders:v1";
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

interface ListingExpiryReminder {
  listingId: string;
  listingTitle: string;
  userId: string;
  notifyAt: number;
  expiresAt: number;
}

function readReminders(): ListingExpiryReminder[] {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(value) ? (value as ListingExpiryReminder[]) : [];
  } catch {
    return [];
  }
}

function writeReminders(reminders: ListingExpiryReminder[]): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(reminders));
    return true;
  } catch {
    return false;
  }
}

export function scheduleListingExpiryReminder(options: {
  listingId: string;
  listingTitle: string;
  userId: string;
  expiresAt: string;
}): boolean {
  const expiresAt = new Date(options.expiresAt).getTime();
  const notifyAt = expiresAt - ONE_DAY_MS;
  if (!Number.isFinite(expiresAt) || notifyAt <= Date.now()) return false;

  const reminder: ListingExpiryReminder = {
    listingId: options.listingId,
    listingTitle: options.listingTitle,
    userId: options.userId,
    notifyAt,
    expiresAt,
  };
  return writeReminders([
    ...readReminders().filter(
      (item) =>
        item.listingId !== reminder.listingId ||
        item.userId !== reminder.userId,
    ),
    reminder,
  ]);
}

export function clearListingExpiryReminders(userId: string): void {
  writeReminders(readReminders().filter((item) => item.userId !== userId));
}

export async function deliverDueListingExpiryReminders(
  userId: string,
): Promise<number> {
  if (wasWebPushDisabled(userId)) return 0;
  const reminders = readReminders();
  const now = Date.now();
  const due = reminders.filter(
    (item) => item.userId === userId && item.notifyAt <= now,
  );
  if (!due.length) return 0;

  if (!("Notification" in window) || Notification.permission !== "granted") {
    writeReminders(
      reminders.filter((item) => item.expiresAt + ONE_DAY_MS * 7 > now),
    );
    return 0;
  }

  const delivered = new Set<string>();
  for (const reminder of due) {
    const title = "Listing Expiry Reminder";
    const body = `Your listing "${reminder.listingTitle}" is expiring soon. Please renew or edit it.`;
    const actionUrl = `/edit-listing/${encodeURIComponent(reminder.listingId)}`;
    const displayed = await showBrowserNotification(title, {
      body,
      icon: "/icons/icon-192.png",
      badge: "/icons/icon-192.png",
      tag: `listing-expiry-${reminder.listingId}`,
      data: {
        listingId: reminder.listingId,
        type: "listing_expiry",
      },
      actionUrl,
    });
    if (displayed) {
      delivered.add(`${reminder.userId}:${reminder.listingId}`);
    }
  }

  writeReminders(
    reminders.filter(
      (item) => !delivered.has(`${item.userId}:${item.listingId}`),
    ),
  );
  return delivered.size;
}
